import React, { useState, useEffect, useMemo } from 'react'
import MaterialIcon from '@ovaeasy/react-native-vector-icons/MaterialIcons'
import * as S from './SearchInvitableRater.styles'
import { useApi } from '@common/hooks/useApi'
import theme from '@app/ui/theme'
import useInvite from '@common/hooks/useInvite'
import { useEvaluation } from '@common/hooks'
import { useSnackbar } from '@app/snackbar'
import Modal, { ModalHeader } from '@app/components/Modal'

const SKELETON_ROW_WIDTHS: Array<[string, string]> = [
  ['58%', '42%'],
  ['46%', '60%'],
  ['64%', '38%'],
  ['52%', '48%'],
  ['44%', '54%'],
  ['60%', '40%'],
]

type Props = {
  isOpen: boolean
  onClose: () => void
}

type InvitableUser = {
  id: number
  forename: string
  lastname: string
  email: string
  gitEmail: string
  level: number
  accountType: number
  golfId: string
}

type FieldValidation = { valid: boolean; message?: string }

const EXAMPLE = 'name@example.com'
const GOLF_ID_EXAMPLE = '850101-001'

const validateEmail = (raw: string): FieldValidation => {
  const email = raw.trim()
  if (!email) return { valid: false }

  if (/\s/.test(email)) {
    return { valid: false, message: "Email addresses can't have spaces" }
  }

  const atCount = (email.match(/@/g) || []).length
  if (atCount === 0) {
    return {
      valid: false,
      message: `Email addresses need an "@" — like ${EXAMPLE}`,
    }
  }
  if (atCount > 1) {
    return {
      valid: false,
      message: 'Email addresses can only have one "@"',
    }
  }

  const [local, domain] = email.split('@')
  if (!local) {
    return {
      valid: false,
      message: `Add the part before the "@" — like ${EXAMPLE}`,
    }
  }
  if (!domain) {
    return {
      valid: false,
      message: `Add the part after the "@" — like ${EXAMPLE}`,
    }
  }

  if (domain.includes(',')) {
    return {
      valid: false,
      message: 'Did you mean a "." instead of a "," ? (e.g. .com, not ,com)',
    }
  }

  if (!domain.includes('.')) {
    return {
      valid: false,
      message: `The part after "@" should include a "." — like ${EXAMPLE}`,
    }
  }

  if (domain.startsWith('.') || domain.endsWith('.')) {
    return {
      valid: false,
      message: `Looks like a "." is in the wrong place — like ${EXAMPLE}`,
    }
  }

  const tld = domain.split('.').pop() || ''
  if (tld.length < 2) {
    return {
      valid: false,
      message: 'The ending after the "." looks too short — like .com or .se',
    }
  }
  if (!/^[a-zA-Z]{2,}$/.test(tld)) {
    return {
      valid: false,
      message:
        'The ending after the "." should only contain letters — like .com or .se',
    }
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return {
      valid: false,
      message: `That doesn't look like a valid email — try something like ${EXAMPLE}`,
    }
  }

  return { valid: true }
}

const validateGolfId = (raw: string): FieldValidation => {
  const id = raw.trim()
  if (!id) return { valid: false }

  if (/\s/.test(id)) {
    return { valid: false, message: "Golf IDs can't have spaces" }
  }
  if (!/^[\d-]+$/.test(id)) {
    return {
      valid: false,
      message: `Golf IDs use only numbers and a "-" — like ${GOLF_ID_EXAMPLE}`,
    }
  }
  if (!id.includes('-')) {
    return {
      valid: false,
      message: `Golf IDs need a "-" — like ${GOLF_ID_EXAMPLE}`,
    }
  }
  if (!/^\d{6}-\d{3}$/.test(id)) {
    return {
      valid: false,
      message: `Golf IDs look like ${GOLF_ID_EXAMPLE} — six digits, a "-", then three digits`,
    }
  }

  return { valid: true }
}

// Golf IDs are only digits and a dash; anything with an "@" or letters is treated as an email.
const validateInvite = (raw: string): FieldValidation => {
  const value = raw.trim()
  if (!value) return { valid: false }
  return /^[\d-]+$/.test(value) ? validateGolfId(value) : validateEmail(value)
}

const SearchInvitableRatersModal = ({ isOpen, onClose }: Props) => {
  const { data, isLoading: fetchingUsers } = useApi<InvitableUser[]>(
    '/evaluations/invitable'
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [touched, setTouched] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [invitedUsers, setInvitedUsers] = useState<number[]>([])
  const [invitedCustomValues, setInvitedCustomValues] = useState<string[]>([])
  const [pendingUserIds, setPendingUserIds] = useState<number[]>([])
  const [pendingCustomValue, setPendingCustomValue] = useState<string | null>(
    null
  )
  const { sendInvite } = useInvite()
  const evaluation = useEvaluation()
  const [openSnackbar] = useSnackbar()

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('')
      setTouched(false)
    }
  }, [isOpen])

  const users = data || []

  const normalizedSearch = searchTerm.trim().toLowerCase()

  const filteredUsers = useMemo(() => {
    if (!normalizedSearch) return users
    return users.filter((user) => {
      const forename = user.forename?.toLowerCase() || ''
      const lastname = user.lastname?.toLowerCase() || ''
      const fullName = `${forename} ${lastname}`.trim()
      const email = user.email?.toLowerCase() || ''
      const gitEmail = user.gitEmail?.toLowerCase() || ''
      const golfId = user.golfId || ''
      return (
        forename.includes(normalizedSearch) ||
        lastname.includes(normalizedSearch) ||
        fullName.includes(normalizedSearch) ||
        email.includes(normalizedSearch) ||
        gitEmail.includes(normalizedSearch) ||
        golfId.includes(searchTerm.trim())
      )
    })
  }, [users, normalizedSearch, searchTerm])

  const inviteValidation = validateInvite(searchTerm)
  const trimmedValue = searchTerm.trim().toLowerCase()
  const alreadyInvitedCustom = invitedCustomValues.includes(trimmedValue)
  const isPendingCustom = pendingCustomValue === trimmedValue
  const showInputError =
    touched && !inviteValidation.valid && !!inviteValidation.message
  const canSendCustomInvite =
    inviteValidation.valid && !alreadyInvitedCustom && !isPendingCustom

  const handleCustomInvite = async () => {
    if (!inviteValidation.valid) {
      setTouched(true)
      return
    }
    if (alreadyInvitedCustom) {
      openSnackbar('An invite has already been sent to this email or Golf ID')
      return
    }
    setPendingCustomValue(trimmedValue)
    try {
      await sendInvite(trimmedValue, evaluation.currentId, null)
      setInvitedCustomValues((prev) =>
        prev.includes(trimmedValue) ? prev : [...prev, trimmedValue]
      )
    } catch (error) {
      openSnackbar("Couldn't send the invite. Please try again.")
    } finally {
      setPendingCustomValue((current) =>
        current === trimmedValue ? null : current
      )
    }
  }

  const handleUserInvite = async (user: InvitableUser) => {
    const target = user.email || user.gitEmail || user.golfId
    if (!target) {
      openSnackbar("This user doesn't have an email or Golf ID on file")
      return
    }
    if (invitedUsers.includes(user.id) || pendingUserIds.includes(user.id)) {
      return
    }
    setPendingUserIds((prev) => [...prev, user.id])
    try {
      await sendInvite(target, evaluation.currentId, null)
      setInvitedUsers((prev) =>
        prev.includes(user.id) ? prev : [...prev, user.id]
      )
    } catch (error) {
      openSnackbar("Couldn't send the invite. Please try again.")
    } finally {
      setPendingUserIds((prev) => prev.filter((id) => id !== user.id))
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSendCustomInvite) {
      e.preventDefault()
      handleCustomInvite()
    }
  }

  const renderCustomInviteAction = () => {
    if (alreadyInvitedCustom) {
      return (
        <S.InvitedLabel>
          <MaterialIcon name="check-circle" size={16} />
          Sent
        </S.InvitedLabel>
      )
    }
    return (
      <S.InviteButton
        type="button"
        onClick={handleCustomInvite}
        disabled={!canSendCustomInvite}
        aria-disabled={!canSendCustomInvite}
        title={
          !inviteValidation.valid
            ? inviteValidation.message ||
              'Type an email or Golf ID to send an invite'
            : 'Send invitation'
        }
      >
        {isPendingCustom ? (
          <>
            <S.Spinner $size={14} />
            <span>Sending</span>
          </>
        ) : (
          <span>Invite</span>
        )}
      </S.InviteButton>
    )
  }

  const renderUserAction = (user: InvitableUser) => {
    if (invitedUsers.includes(user.id)) {
      return (
        <S.InvitedLabel>
          <MaterialIcon name="check-circle" size={16} />
          Invitation Sent
        </S.InvitedLabel>
      )
    }
    const pending = pendingUserIds.includes(user.id)
    return (
      <S.InviteButton
        type="button"
        onClick={() => handleUserInvite(user)}
        disabled={pending}
      >
        {pending ? (
          <>
            <S.Spinner $size={14} />
            <span>Sending</span>
          </>
        ) : (
          <span>Invite</span>
        )}
      </S.InviteButton>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="600px" height="640px">
      <ModalHeader>
        <S.HeaderInner>
          <S.Title>Invite Rater</S.Title>
          <S.Subtitle>
            Invite a team member using the same detail they sign in with — their
            email if they sign in with email, or their Golf ID if they sign in
            with Golf ID.
          </S.Subtitle>
        </S.HeaderInner>
      </ModalHeader>

      <S.Body>
        <S.InputRow>
          <S.InputField $hasError={showInputError} $isFocused={isFocused}>
            <MaterialIcon
              name="search"
              size={18}
              color={theme.colors.secondary}
            />
            <S.SearchInput
              type="text"
              enterKeyHint="send"
              placeholder="Enter email or Golf ID"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                if (!touched) setTouched(true)
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
            {renderCustomInviteAction()}
          </S.InputField>

          {showInputError ? (
            <S.InlineError>
              <MaterialIcon
                name="error-outline"
                size={14}
                color={theme.colors.warning}
              />
              {inviteValidation.message}
            </S.InlineError>
          ) : alreadyInvitedCustom ? (
            <S.Hint>
              An invite has already been sent to <strong>{trimmedValue}</strong>
              .
            </S.Hint>
          ) : (
            <S.Hint>
              {fetchingUsers
                ? ' '
                : searchTerm
                ? `${filteredUsers.length} ${
                    filteredUsers.length === 1 ? 'match' : 'matches'
                  } in your organisation`
                : 'Type an email or Golf ID and press Enter to send an invite'}
            </S.Hint>
          )}
        </S.InputRow>

        <S.SectionLabel>Existing users</S.SectionLabel>

        <S.UsersList aria-busy={fetchingUsers}>
          {fetchingUsers ? (
            <>
              {SKELETON_ROW_WIDTHS.map((widths, i) => (
                <S.SkeletonRow key={i} aria-hidden="true">
                  <S.SkeletonText>
                    <S.SkeletonBar $width={widths[0]} $height={15} />
                    <S.SkeletonBar $width={widths[1]} $height={12} />
                  </S.SkeletonText>
                  <S.SkeletonButton />
                </S.SkeletonRow>
              ))}
            </>
          ) : (
            <>
              {filteredUsers.map((user) => (
                <S.UserItem key={user.id}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <S.UserName>
                      {`${user.forename || '-'} ${user.lastname || ''}`.trim()}
                      {user.golfId ? ` (${user.golfId})` : ''}
                    </S.UserName>
                    <S.UserEmail>
                      {user.email || user.gitEmail || 'No email on file'}
                    </S.UserEmail>
                  </div>
                  {renderUserAction(user)}
                </S.UserItem>
              ))}
              {filteredUsers.length === 0 && (
                <S.NoResults>
                  No one matches your search.
                  {inviteValidation.valid && !alreadyInvitedCustom
                    ? ' Press Enter to send an invite to this email or Golf ID instead.'
                    : ''}
                </S.NoResults>
              )}
            </>
          )}
        </S.UsersList>
      </S.Body>
    </Modal>
  )
}

export default SearchInvitableRatersModal
